import React, { useCallback, useEffect } from "react";
import moment from "moment";
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Divider,
    Grid2,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Stack,
    Typography,
    Tooltip,
    IconButton,
    Button,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import DirectionsWalkRoundedIcon from "@mui/icons-material/DirectionsWalkRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import BedtimeRoundedIcon from "@mui/icons-material/BedtimeRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import RefreshIcon from "@mui/icons-material/Refresh";
import { hasAccess, httpRequest, onRequestError } from "../../helper/global";
import { PERMISSIONS } from "../../helper/constant";
import { hideLoader, showLoader } from "../../reducers/loaderSlice";
import { set } from "date-fns";
import { ca } from "date-fns/locale";

/**
 * Main UI
 */
export function CoachDiscussion({
    dispatch,
    navigate,
    theme,
    user,
    permissionConfig,
    patient,
}) {
    const [coachData, setCoachData] = React.useState(null);
    const [daysAgo, setDaysAgo] = React.useState(30);
    const [windowLabel, setWindowLabel] = React.useState("");

    const onRefresh = useCallback(
        async (cache = false) => {
            if (!user || !permissionConfig) {
                return;
            }

            if (
                !hasAccess(
                    user,
                    permissionConfig,
                    "healthData",
                    PERMISSIONS.read,
                )
            ) {
                return;
            }

            try {
                dispatch(showLoader({ message: "Analyzing health data..." }));
                const result = await httpRequest(
                    `ai/request/analyze-data`,
                    {
                        patientId: patient.id,
                        daysAgo: daysAgo,
                        cache: cache,
                    },
                    "POST",
                    (error) => {
                        dispatch(hideLoader());
                        onRequestError(dispatch, navigate, {
                            title: error.title,
                            message: error.message,
                        });
                    },
                );

                dispatch(hideLoader());

                if (result === false) {
                    return;
                }

                setCoachData(result.payload);
                setWindowLabel(`Last ${daysAgo} days`);
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        },
        [daysAgo],
    );

    useEffect(() => {
        onRefresh(true);
    }, []);

    // If no coach data, show empty state with analyze button
    if (
        !coachData ||
        !coachData["coach_discussion"] ||
        (coachData["coach_discussion"].length > 0 &&
            !coachData["coach_discussion"][0].topic)
    ) {
        return (
            <Card
                elevation={0}
                sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 3,
                    overflow: "hidden",
                }}
            >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box
                        sx={{
                            textAlign: "center",
                            py: 4,
                        }}
                    >
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                        >
                            No Coach Discussions available.
                        </Typography>

                        {hasAccess(
                            user,
                            permissionConfig,
                            "ai",
                            PERMISSIONS.write,
                        ) && (
                            <Button
                                variant="contained"
                                onClick={() => onRefresh()}
                                startIcon={<RefreshIcon />}
                            >
                                Generate Discussion
                            </Button>
                        )}
                    </Box>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card
            elevation={0}
            sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
                overflow: "hidden",
            }}
        >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                    Discussion Points
                </Typography>
                <List disablePadding>
                    {coachData.coach_discussion.map((item, index) => (
                        <React.Fragment key={index}>
                            <ListItem
                                alignItems="flex-start"
                                sx={{ px: 0, py: 1 }}
                            >
                                <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                                    <CheckCircleRoundedIcon
                                        fontSize="small"
                                        color="primary"
                                    />
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        typeof item === "object" &&
                                        item !== null ? (
                                            <Stack spacing={0.75}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.primary"
                                                >
                                                    <Box
                                                        component="span"
                                                        sx={{ fontWeight: 600 }}
                                                    >
                                                        Topic:
                                                    </Box>{" "}
                                                    {item.topic || "-"}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.primary"
                                                >
                                                    <Box
                                                        component="span"
                                                        sx={{ fontWeight: 600 }}
                                                    >
                                                        Data Insight:
                                                    </Box>{" "}
                                                    {item["data_insight"] ||
                                                        "-"}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.primary"
                                                >
                                                    <Box
                                                        component="span"
                                                        sx={{ fontWeight: 600 }}
                                                    >
                                                        Coach Focus:
                                                    </Box>{" "}
                                                    {item["coach_focus"] ||
                                                        item.coachFocus ||
                                                        "-"}
                                                </Typography>
                                            </Stack>
                                        ) : (
                                            <Typography
                                                variant="body2"
                                                color="text.primary"
                                            >
                                                {item}
                                            </Typography>
                                        )
                                    }
                                />
                            </ListItem>
                            {index < coachData.coach_discussion.length - 1 && (
                                <Divider component="li" />
                            )}
                        </React.Fragment>
                    ))}
                </List>

                {coachData.coach_discussion_summary && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                mb: 1,
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight={600}>
                                Discussion Summary
                            </Typography>
                            <Tooltip title="Copy summary">
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        navigator.clipboard.writeText(
                                            coachData.coach_discussion_summary,
                                        )
                                    }
                                >
                                    <ContentCopyRoundedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Typography variant="body2" color="text.primary">
                            {coachData.coach_discussion_summary}
                        </Typography>
                    </>
                )}

                {coachData.educational_materials &&
                    coachData.educational_materials.length > 0 && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Typography
                                variant="subtitle1"
                                fontWeight={600}
                                sx={{ mb: 1 }}
                            >
                                Educational Materials
                            </Typography>
                            <List disablePadding>
                                {coachData.educational_materials.map(
                                    (material, index) => (
                                        <React.Fragment key={index}>
                                            <ListItem
                                                alignItems="flex-start"
                                                sx={{ px: 0, py: 1 }}
                                            >
                                                <ListItemIcon
                                                    sx={{
                                                        minWidth: 36,
                                                        mt: 0.5,
                                                    }}
                                                >
                                                    <TipsAndUpdatesRoundedIcon
                                                        fontSize="small"
                                                        color="primary"
                                                    />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems:
                                                                    "center",
                                                                gap: 0.5,
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                component="a"
                                                                href={
                                                                    material.url
                                                                }
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                sx={{
                                                                    color: "primary.main",
                                                                    textDecoration:
                                                                        "none",
                                                                    "&:hover": {
                                                                        textDecoration:
                                                                            "underline",
                                                                    },
                                                                }}
                                                            >
                                                                {material.name}{" "}
                                                                (
                                                                {material.url ||
                                                                    ""}
                                                                )
                                                            </Typography>
                                                            <Tooltip title="Copy URL">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() =>
                                                                        navigator.clipboard.writeText(
                                                                            material.url ||
                                                                                "",
                                                                        )
                                                                    }
                                                                >
                                                                    <ContentCopyRoundedIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    }
                                                />
                                            </ListItem>
                                            {index <
                                                coachData.educational_materials
                                                    .length -
                                                    1 && (
                                                <Divider component="li" />
                                            )}
                                        </React.Fragment>
                                    ),
                                )}
                            </List>
                        </>
                    )}
            </CardContent>
        </Card>
    );
}
